import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type RobotRole =
  | 'accueil'
  | 'assistance_personnelle'
  | 'securite'
  | 'retail'
  | 'aide_domicile'
  | 'evenementiel'
  | 'industrie_legere'
  | 'sur_mesure'

export type EnvironmentType = 'indoor' | 'outdoor' | 'both' | 'hazardous'

export type MissionType =
  | 'navigation'
  | 'manipulation'
  | 'speech_interaction'
  | 'surveillance'
  | 'assistance'
  | 'transport'
  | 'inspection'
  | 'custom_task'

export type AutonomyLevel = 1 | 2 | 3 | 4 | 5

export type AppearanceStyle = 'humanoid' | 'industrial' | 'hybrid' | 'custom'

export type BudgetBand = 'companion' | 'assistant_pro' | 'operator_specialist'

export type ChargingMethod = 'auto_dock' | 'manual'

export interface WizardDraft {
  step1_role?: RobotRole
  step2_environment?: EnvironmentType
  step3_missions?: MissionType[]
  step4_ai_level?: AutonomyLevel
  step5_height_cm?: number
  step5_payload_kg?: number
  step5_ip_rating?: 'ip44' | 'ip54' | 'ip65' | 'ip67'
  step6_appearance?: AppearanceStyle
  step6_color_accent?: string
  step7_hours_per_day?: number
  step7_charging?: ChargingMethod
  step8_budget?: BudgetBand
}

export const TOTAL_STEPS = 10

function canProceed(step: number, draft: WizardDraft): boolean {
  switch (step) {
    case 1: return !!draft.step1_role
    case 2: return !!draft.step2_environment
    case 3: return (draft.step3_missions?.length ?? 0) >= 1
    case 4: return !!draft.step4_ai_level
    case 5: return draft.step5_height_cm != null && draft.step5_payload_kg != null
    case 6: return !!draft.step6_appearance
    case 7: return draft.step7_hours_per_day != null && !!draft.step7_charging
    case 8: return !!draft.step8_budget
    case 9: return false // auth gate — handled externally
    case 10: return true
    default: return false
  }
}

interface WizardState {
  currentStep: number
  draft: WizardDraft
  isAuthenticated: boolean
  setCurrentStep: (step: number) => void
  goNext: () => void
  goPrev: () => void
  update: (fields: Partial<WizardDraft>) => void
  setAuthenticated: (value: boolean) => void
  canProceedCurrent: () => boolean
  reset: () => void
}

export const useWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      draft: {},
      isAuthenticated: false,

      setCurrentStep: (step) => set({ currentStep: step }),

      goNext: () => {
        const { currentStep, draft } = get()
        if (currentStep < TOTAL_STEPS && canProceed(currentStep, draft)) {
          set({ currentStep: currentStep + 1 })
        }
      },

      goPrev: () => {
        const { currentStep } = get()
        if (currentStep > 1) set({ currentStep: currentStep - 1 })
      },

      update: (fields) =>
        set((state) => ({ draft: { ...state.draft, ...fields } })),

      setAuthenticated: (value) => set({ isAuthenticated: value }),

      canProceedCurrent: () => {
        const { currentStep, draft, isAuthenticated } = get()
        if (currentStep === 9) return isAuthenticated
        return canProceed(currentStep, draft)
      },

      reset: () => set({ currentStep: 1, draft: {}, isAuthenticated: false }),
    }),
    {
      name: 'rfl-wizard-draft',
      partialize: (state) => ({ draft: state.draft, currentStep: state.currentStep }),
    }
  )
)
